"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  IdentityPresignRequest,
  IdentitySubmitRequest,
  PhoneVerifyConfirmRequest,
  PhoneVerifyStartRequest,
} from "@chotca/config";
import { getSdk } from "../sdk";
import { qk } from "../query-keys";

// Self account identity (CCCD) — SCRUM-71.
export function useMyIdentity() {
  return useQuery({
    queryKey: qk.identity.mine,
    queryFn: () => getSdk().identity.getMyIdentity(),
    staleTime: 30_000,
  });
}

export function usePresignIdentity() {
  return useMutation({
    mutationFn: (body: IdentityPresignRequest) =>
      getSdk().identity.presignIdentity(body),
  });
}

export function useSubmitIdentity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: IdentitySubmitRequest) =>
      getSdk().identity.submitIdentity(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.identity.mine });
      qc.invalidateQueries({ queryKey: qk.me });
    },
  });
}

export function useWithdrawIdentity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => getSdk().identity.withdrawIdentity(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.identity.mine });
    },
  });
}

// Account email verification — re-sends the verify link to the user's email.
// No body: the endpoint reads the caller from the bearer token.
export function useRequestEmailVerify() {
  return useMutation({
    mutationFn: () => getSdk().auth.requestEmailVerify(),
  });
}

// Account phone verification.
export function useStartPhoneVerify() {
  return useMutation({
    mutationFn: (body: PhoneVerifyStartRequest) =>
      getSdk().identity.startPhoneVerify(body),
  });
}

export function useConfirmPhoneVerify() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PhoneVerifyConfirmRequest) =>
      getSdk().identity.confirmPhoneVerify(body),
    onSuccess: () => {
      // phone + identityLevel changed — refresh the current user.
      qc.invalidateQueries({ queryKey: qk.me });
      // Rep reaching U1 can raise the org's verifyLevel to L1 (SCRUM-74).
      qc.invalidateQueries({ queryKey: ["orgs"] });
    },
  });
}
